/**
 * <LivretImportDialog />
 *
 * Côté élève : dialogue de confirmation pour appliquer un payload de
 * validations REMC envoyé par l'enseignant via lien `?import=<base64>`.
 *
 * Décode le payload, affiche la liste avec statuts et compétences officielles,
 * permet à l'élève d'appliquer en un clic (boucle d'updates) puis nettoie l'URL.
 *
 * Props :
 *  - payload : { v, ts, items: {code: status}, student?, context? }
 *  - open : boolean
 *  - onOpenChange(boolean)
 *  - onApply(items) : callback qui appelle les updates en parallèle
 */
import React, { useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { BookOpen, Circle, Clock, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { getLivretItem } from '../../data/teacher/evalInference';

const CATEGORIE_COLORS = {
  C1: '#0891b2', C2: '#7c3aed', C3: '#dc2626', C4: '#16a34a',
};
const STATUS_META = {
  non_acquis: { label: 'À travailler', color: '#64748b', Icon: Circle },
  en_cours:   { label: 'En cours',     color: '#d97706', Icon: Clock },
  acquis:     { label: 'Acquis',       color: '#16a34a', Icon: CheckCircle2 },
};
const VALID_STATUSES = new Set(['non_acquis', 'en_cours', 'acquis']);

function formatDateLabel(ts) {
  try {
    const d = new Date(ts || Date.now());
    return d.toLocaleString('fr-FR', { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_e) {
    return '';
  }
}

export default function LivretImportDialog({ payload, open, onOpenChange, onApply }) {
  const [applying, setApplying] = useState(false);

  if (!payload) return null;

  // Filtre items invalides
  const items = Object.entries(payload.items || {}).filter(
    ([code, status]) => getLivretItem(code) && VALID_STATUSES.has(status),
  );
  const invalidCount = Object.keys(payload.items || {}).length - items.length;

  const dateLabel = formatDateLabel(payload.ts);

  const handleApply = async () => {
    setApplying(true);
    try {
      await onApply(items);
    } finally {
      setApplying(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px]" data-testid="livret-import-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="w-5 h-5 text-[#1d4ed8]" />
            Importer les validations REMC de la séance
          </DialogTitle>
          <DialogDescription className="space-y-1">
            <span>
              Ton enseignant te transmet <strong>{items.length}</strong>{' '}
              validation{items.length > 1 ? 's' : ''} REMC à appliquer dans ton livret.
            </span>
            {payload.context && (
              <span className="block text-[11px] italic">
                Session : <strong>{payload.context}</strong>
                {dateLabel ? ` · ${dateLabel}` : ''}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>

        {items.length === 0 ? (
          <div className="rounded-xl border-2 border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-amber-500" />
            Aucune validation valide n&apos;a été trouvée dans ce lien.
          </div>
        ) : (
          <ul
            className="space-y-1.5 max-h-[280px] overflow-y-auto pr-1"
            data-testid="livret-import-list"
          >
            {items.map(([code, status]) => {
              const item = getLivretItem(code);
              const catColor = CATEGORIE_COLORS[item.categorie] || '#1d4ed8';
              const meta = STATUS_META[status];
              const Icon = meta.Icon;
              return (
                <li
                  key={code}
                  className="flex items-center gap-2 rounded-lg border border-border bg-background/40 px-2.5 py-2"
                  data-testid={`import-row-${code}`}
                >
                  <span
                    className="inline-flex items-center justify-center rounded-md px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white flex-shrink-0 min-w-[42px]"
                    style={{ background: catColor }}
                  >
                    {code}
                  </span>
                  <span className="text-xs sm:text-sm font-semibold leading-snug flex-1 min-w-0">
                    {item.intitule}
                  </span>
                  <span
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider text-white flex-shrink-0"
                    style={{ background: meta.color }}
                  >
                    <Icon className="w-3 h-3" />
                    {meta.label}
                  </span>
                </li>
              );
            })}
          </ul>
        )}

        {invalidCount > 0 && (
          <p className="text-[11px] italic text-amber-600 dark:text-amber-400">
            <AlertTriangle className="inline w-3 h-3 mr-1" />
            {invalidCount} entrée{invalidCount > 1 ? 's' : ''} ignorée{invalidCount > 1 ? 's' : ''} (codes ou statuts invalides).
          </p>
        )}

        <p className="text-[11px] italic text-muted-foreground">
          L&apos;import remplace le statut actuel pour chaque compétence concernée. Tes
          notes personnelles sont conservées.
        </p>

        <DialogFooter className="gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applying}
            data-testid="import-cancel-btn"
          >
            Plus tard
          </Button>
          <Button
            type="button"
            onClick={handleApply}
            disabled={applying || items.length === 0}
            className="bg-[#1d4ed8] gap-1.5"
            data-testid="import-apply-btn"
          >
            {applying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
            {applying ? 'Application…' : `Importer dans mon livret`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
